import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { X, Plus, Mail, ArrowRight, Smartphone } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Reusable "Email PDF" dialog: multi-select from the address book + free-form additions.
//
// Props:
//   open, onOpenChange, title, description, suggestedEmails (string[] preselected)
//   onSend(emails: string[]) -> Promise — called when user clicks Next.
//                              Caller is responsible for handing the PDF off
//                              to the device email app via `shareOrMailtoPdf`.
export default function EmailRecipientDialog({
  open, onOpenChange, title = "Email PDF", description, suggestedEmails = [], onSend,
}) {
  const [groups, setGroups] = useState([]);
  const [picked, setPicked] = useState(new Set());
  const [extra, setExtra] = useState([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/email-recipients");
        setGroups(data);
        setPicked(new Set(suggestedEmails.filter(Boolean)));
        setExtra([]);
        setDraft("");
      } catch (e) { toast.error(formatApiError(e)); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggle = (email) => setPicked((s) => {
    const n = new Set(s);
    n.has(email) ? n.delete(email) : n.add(email);
    return n;
  });

  const addManual = () => {
    const e = draft.trim().toLowerCase();
    if (!e) return;
    if (!EMAIL_RX.test(e)) { toast.error("That email looks invalid"); return; }
    if (extra.includes(e) || picked.has(e)) { toast("Already added"); setDraft(""); return; }
    setExtra((p) => [...p, e]);
    setDraft("");
  };

  const removeExtra = (e) => setExtra((p) => p.filter((x) => x !== e));

  const allEmails = useMemo(() => Array.from(new Set([...picked, ...extra])), [picked, extra]);

  const submit = async () => {
    if (allEmails.length === 0) { toast.error("Add at least one recipient"); return; }
    setBusy(true);
    try {
      await onSend(allEmails);
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="email-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Mail className="w-5 h-5 text-coral" /> {title}
          </DialogTitle>
          {description && <DialogDescription className="text-sm">{description}</DialogDescription>}
        </DialogHeader>

        {/* Selected chips */}
        <div className="rounded-2xl border-2 p-3 min-h-[60px]" data-testid="email-recipients-chips">
          <Label className="text-xs uppercase tracking-wider font-bold mb-1.5 block">Recipients · {allEmails.length}</Label>
          <div className="flex flex-wrap gap-1.5">
            {allEmails.length === 0 && <span className="text-xs text-muted-foreground italic">Pick from the list below or type an email.</span>}
            {Array.from(picked).map((e) => (
              <Chip key={`p-${e}`} email={e} onRemove={() => toggle(e)} testId={`recipient-chip-${e}`} />
            ))}
            {extra.map((e) => (
              <Chip key={`x-${e}`} email={e} manual onRemove={() => removeExtra(e)} testId={`recipient-chip-extra-${e}`} />
            ))}
          </div>
        </div>

        {/* Manual entry */}
        <div className="flex gap-2 items-end">
          <div className="flex-1 space-y-1.5">
            <Label className="text-xs uppercase tracking-wider font-bold">Add another email</Label>
            <Input value={draft} onChange={(e) => setDraft(e.target.value)} type="email"
                   onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addManual(); } }}
                   placeholder="someone@example.com" className="rounded-xl min-h-[48px]"
                   data-testid="email-manual-input" />
          </div>
          <Button onClick={addManual} variant="outline" className="rounded-full font-bold gap-1.5 min-h-[48px]"
                  data-testid="email-manual-add-btn"><Plus className="w-4 h-4" /> Add</Button>
        </div>

        {/* Address book by group */}
        <div className="space-y-3">
          {groups.map((g) => (
            <div key={g.label}>
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-1.5">{g.label}</div>
              <div className="grid sm:grid-cols-2 gap-1.5">
                {g.people.map((p) => {
                  const on = picked.has(p.email);
                  return (
                    <button key={p.id} type="button" onClick={() => toggle(p.email)}
                            className={`text-left rounded-2xl border-2 p-2.5 transition flex items-center gap-2 ${
                              on ? "bg-coral/10 border-coral text-foreground"
                                 : "bg-background border-input hover:border-coral/40"
                            }`}
                            data-testid={`recipient-option-${p.email}`}>
                      <span className={`w-4 h-4 rounded border-2 flex-shrink-0 ${on ? "bg-coral border-coral" : "border-muted-foreground/40"}`} />
                      <div className="min-w-0 flex-1">
                        <div className="font-bold text-sm truncate">{p.name}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          {p.email}{p.role && p.role !== p.name ? ` · ${p.role}` : ""}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          {groups.length === 0 && (
            <p className="text-xs text-muted-foreground italic">
              No saved contacts yet. Add a plan manager, support workers, treating medical staff, or family &amp; friends to populate this list.
            </p>
          )}
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-row">
          <p className="flex-1 text-xs text-muted-foreground flex items-start gap-1.5 mr-2">
            <Smartphone className="w-3.5 h-3.5 mt-0.5 shrink-0 text-coral" />
            <span>Opens your device's email app with the PDF attached. On desktop browsers without share support, the PDF will download and you'll attach it manually.</span>
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full font-bold" data-testid="email-cancel-btn">Cancel</Button>
            <Button onClick={submit} disabled={busy || allEmails.length === 0}
                    className="rounded-full font-bold gap-2"
                    style={{ background: "hsl(var(--coral))", color: "white" }}
                    data-testid="email-send-btn">
              {busy ? "Opening…" : <>Next <ArrowRight className="w-4 h-4" /></>}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Chip({ email, manual, onRemove, testId }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold ${
      manual ? "bg-coral/15 text-coral border-2 border-coral/30" : "bg-coral/10 text-foreground border-2 border-coral/20"
    }`} data-testid={testId}>
      {email}
      <button onClick={onRemove} className="opacity-60 hover:opacity-100" aria-label="Remove">
        <X className="w-3 h-3" />
      </button>
    </span>
  );
}
